import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, ArrowRight, Tag } from 'lucide-react';

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  image: string;
  date: string;
  readTime: string;
  slug: string;
  category: string;
  featured?: boolean;
}

const blogPosts: BlogPost[] = [
  {
    id: '1',
    title: "The Importance of Regular Car Maintenance in Dubai's Climate",
    excerpt: "Dubai's unique climate presents specific challenges for vehicle maintenance. Learn how to protect your car from the heat and dust.",
    content: "Dubai's scorching heat and dusty roads can take a serious toll on your vehicle. Regular maintenance isn't just a suggestion—it's a necessity. From engine overheating to battery drainage and worn-out tires, neglecting your car in this climate can lead to major repair costs. At SollBridge, we understand the challenges your vehicle faces. That's why our service packages are designed to keep your car running smoothly year-round. We offer regular oil changes, AC inspections, coolant checks, and more—ensuring your safety and prolonging your vehicle's life.",
    image: "https://images.unsplash.com/photo-1632823471565-1ecdf5c900cb?auto=format&fit=crop&q=80",
    date: "2024-02-25",
    readTime: "5 min read",
    slug: "importance-of-regular-car-maintenance",
    category: "Maintenance",
    featured: true
  },
  {
    id: '2',
    title: "Top 5 Signs Your Car Needs Immediate Servicing",
    excerpt: "Learn to recognize the warning signs that indicate your car needs immediate attention. Don't let small issues become major problems.",
    content: "Ignoring warning signs can turn minor issues into costly repairs. Here are five red flags to watch for: unusual noises, dashboard warning lights, leaking fluids, reduced fuel efficiency, and vibration or pulling while driving.",
    image: "https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?auto=format&fit=crop&q=80",
    date: "2024-02-24",
    readTime: "4 min read",
    slug: "top-5-signs-your-car-needs-servicing",
    category: "Diagnostics"
  },
  {
    id: '3',
    title: "How to Choose the Right Car Service Package",
    excerpt: "Find the perfect service package for your vehicle's needs. Learn about different maintenance schedules and what they include.",
    content: "Choosing the right service package can save you money and prevent breakdowns. For daily drivers, a basic maintenance package every 5,000 km may suffice. For luxury cars or vehicles over 5 years old, a premium or full-service package is ideal. At SollBridge, we offer tailored packages for all car types—from Japanese to German makes. Not sure what you need? Our experts can assess your car's condition and recommend the perfect fit.",
    image: "https://images.unsplash.com/photo-1530046339160-ce3e530c7d2f?auto=format&fit=crop&q=80",
    date: "2024-02-23",
    readTime: "6 min read",
    slug: "choosing-right-service-package",
    category: "Service Packages"
  },
  {
    id: '4',
    title: "The Benefits of Professional Fluid Top-Ups",
    excerpt: "Discover why professional fluid maintenance is crucial for your vehicle's performance and longevity.",
    content: "Fluids are the lifeblood of your vehicle. Engine oil, brake fluid, coolant, transmission fluid, and windshield washer fluid all play a key role in your car's performance. Topping up fluids without proper knowledge can do more harm than good. At SollBridge, we use manufacturer-recommended fluids and ensure every system is checked thoroughly. Regular fluid top-ups enhance performance, prevent overheating, and extend the life of critical components.",
    image: "https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?auto=format&fit=crop&q=80",
    date: "2024-02-22",
    readTime: "4 min read",
    slug: "benefits-of-professional-fluid-maintenance",
    category: "Maintenance"
  },
  {
    id: '5',
    title: "Why Timely Oil Changes Are Crucial for Engine Health",
    excerpt: "Learn why regular oil changes are essential for your engine's longevity and performance, especially in Dubai's climate.",
    content: "Oil changes are often overlooked—but they're one of the most important things you can do for your engine. Fresh oil reduces friction, keeps your engine clean, and prevents wear. In Dubai's harsh climate, oil can degrade faster than usual, making timely changes even more essential. At SollBridge, we provide fast, affordable oil change services using premium oils suitable for all car brands.",
    image: "https://images.unsplash.com/photo-1635764749881-b158d8e92a8f?auto=format&fit=crop&q=80",
    date: "2024-02-21",
    readTime: "4 min read",
    slug: "importance-of-timely-oil-changes",
    category: "Maintenance"
  },
  {
    id: '6',
    title: "Understanding Brake System Maintenance",
    excerpt: "Everything you need to know about maintaining your car's brake system for optimal safety and performance.",
    content: "Brakes are your car's most important safety system. Regular brake maintenance ensures quicker stops and better control. Warning signs of brake issues include squealing sounds, a soft pedal, and longer stopping distances. SollBridge technicians inspect brake pads, rotors, and brake fluid with every service. Whether it's a simple pad replacement or a full system overhaul, we've got you covered.",
    image: "https://images.unsplash.com/photo-1486006920555-c77dcf18193c?auto=format&fit=crop&q=80",
    date: "2024-02-20",
    readTime: "5 min read",
    slug: "brake-system-maintenance-guide",
    category: "Safety"
  },
  {
    id: '7',
    title: "Preparing Your Car for the Summer Heat in Dubai",
    excerpt: "Essential tips to ensure your vehicle can handle Dubai's extreme summer temperatures effectively.",
    content: "Dubai summers can exceed 45°C, and your car needs to be ready. Make sure your coolant is full, AC is functioning properly, tires are in good condition, and your battery is tested. At SollBridge, we offer a special summer prep package to help your car beat the heat. Don't wait until it's too late—book your service before the heatwave hits.",
    image: "https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?auto=format&fit=crop&q=80",
    date: "2024-02-19",
    readTime: "6 min read",
    slug: "summer-car-preparation-guide",
    category: "Seasonal Care"
  },
  {
    id: '8',
    title: "The Advantages of Choosing SollBridge for Your Car Care Needs",
    excerpt: "Discover why SollBridge is Dubai's trusted choice for comprehensive automotive care and maintenance.",
    content: "Why do hundreds of Dubai residents trust SollBridge? Because we combine quality, convenience, and affordability. Our certified mechanics use top-grade tools and parts, and our transparent pricing means no hidden fees. Plus, our online booking system makes scheduling easy. We treat every car like it's our own, ensuring customer satisfaction every step of the way.",
    image: "https://images.unsplash.com/photo-1625047509168-a7026f36de04?auto=format&fit=crop&q=80",
    date: "2024-02-18",
    readTime: "5 min read",
    slug: "why-choose-sollbridge",
    category: "Company"
  },
  {
    id: '9',
    title: "Common Myths About Car Maintenance Debunked",
    excerpt: "We separate fact from fiction to help you make informed decisions about your car's maintenance.",
    content: "You don't need to change your oil until 10,000 km. False. In Dubai's climate, you may need changes sooner. Premium fuel makes your car faster. Not true—unless your manufacturer recommends it. At SollBridge, we believe in educating our customers. Trust the experts, not myths. Book a check-up and let us walk you through what your car really needs.",
    image: "https://images.unsplash.com/photo-1493238792000-8113da705763?auto=format&fit=crop&q=80",
    date: "2024-02-17",
    readTime: "4 min read",
    slug: "car-maintenance-myths-debunked",
    category: "Education"
  },
  {
    id: '10',
    title: "A Step-by-Step Guide to SollBridge's Service Process",
    excerpt: "Learn how our streamlined service process makes car maintenance hassle-free and convenient.",
    content: "First, visit our website to choose a service and book an appointment. Next, drop off your vehicle or use our pickup option. Our technicians will inspect your vehicle and begin servicing. Once completed, we'll notify you and share a report. Payment can be made online or at pickup. It's quick, reliable, and hassle-free—just the way car care should be. Experience the SollBridge difference today.",
    image: "https://images.unsplash.com/photo-1517524008697-84bbe3c3fd98?auto=format&fit=crop&q=80",
    date: "2024-02-16",
    readTime: "3 min read",
    slug: "sollbridge-service-process",
    category: "Service Guide"
  }
];

export function BlogPage() {
  const featuredPost = blogPosts.find(post => post.featured);
  const regularPosts = blogPosts.filter(post => !post.featured);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-800">
      {/* Hero Section */}
      <div className="relative bg-cover bg-center h-[300px]"
        style={{
          backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url("https://images.unsplash.com/photo-1625047509168-a7026f36de04?auto=format&fit=crop&q=80")'
        }}>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white px-4">
            <h1 className="text-4xl font-bold mb-4">Auto Care Insights</h1>
            <p className="text-xl max-w-2xl">
              Expert advice and tips for maintaining your vehicle in Dubai
            </p>
          </div>
        </div>
      </div>

      {/* Blog Posts Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Featured Post */}
        {featuredPost && (
          <div className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">Featured Story</h2>
            <Link
              to={`/blog/${featuredPost.slug}`}
              className="group grid md:grid-cols-2 gap-8 bg-white dark:bg-gray-700 rounded-lg shadow-lg overflow-hidden"
            >
              <div 
                className="h-64 md:h-full bg-cover bg-center transform transition-transform group-hover:scale-105"
                style={{ backgroundImage: `url(${featuredPost.image})` }}
              />
              <div className="p-8">
                <div className="flex items-center space-x-4 mb-4">
                  <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 rounded-full text-sm">
                    {featuredPost.category}
                  </span>
                  <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                    <Clock className="w-4 h-4 mr-1" />
                    <span>{featuredPost.readTime}</span>
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                  {featuredPost.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-6">
                  {featuredPost.excerpt}
                </p>
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    <Calendar className="w-4 h-4 inline mr-2" />
                    {new Date(featuredPost.date).toLocaleDateString()}
                  </div>
                  <div className="flex items-center text-blue-600 dark:text-blue-400">
                    <span>Read article</span>
                    <ArrowRight className="w-4 h-4 ml-2 transform group-hover:translate-x-2 transition-transform" />
                  </div>
                </div>
              </div>
            </Link>
          </div>
        )}

        {/* Regular Posts */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {regularPosts.map((post) => (
            <Link
              key={post.id}
              to={`/blog/${post.slug}`}
              className="group bg-white dark:bg-gray-700 rounded-lg shadow-lg overflow-hidden transform transition-all hover:-translate-y-1 hover:shadow-xl"
            >
              <div 
                className="h-48 bg-cover bg-center transform transition-transform group-hover:scale-105"
                style={{ backgroundImage: `url(${post.image})` }}
              />
              <div className="p-6">
                <div className="flex items-center space-x-2 mb-4">
                  <Tag className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {post.category}
                  </span>
                </div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                  {post.title}
                </h2>
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  {post.excerpt}
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                    <Calendar className="w-4 h-4 mr-1" />
                    <span>{new Date(post.date).toLocaleDateString()}</span>
                  </div>
                  <div className="flex items-center text-blue-600 dark:text-blue-400">
                    <span>Read more</span>
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}